import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Download, KanbanSquare, List, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { ENQ_STATUSES, LEAD_DESTINATIONS, LEAD_SOURCES, TRIP_TYPES } from "@/lib/constants";
import { SORTS, downloadLeadsCsv, isDue, sortLeads, tomorrowISO } from "@/lib/leads";
import { Empty, PageHeader, Panel } from "@/components/admin/common";
import { BookingDialog } from "@/components/admin/BookingDialog";
import { LeadStats } from "@/components/admin/leads/LeadStats";
import { LeadBoard } from "@/components/admin/leads/LeadBoard";
import { LeadTable } from "@/components/admin/leads/LeadTable";
import { AddLeadDialog } from "@/components/admin/leads/AddLeadDialog";
import { useSiteData } from "@/context/SiteDataContext";

const Filter = ({ value, onChange, options, all, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-10 w-full sm:w-[170px]" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent className="bg-white"><SelectItem value="all">{all}</SelectItem>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
  </Select>
);

const matches = (l, f) => {
  const q = f.q.trim().toLowerCase();
  return (f.status === "all" || l.status === f.status) && (f.trip === "all" || l.trip_type === f.trip) && (f.source === "all" || (l.source || "website") === f.source)
    && (f.dest === "all" || l.destination === f.dest) && (f.pkg === "all" || l.package_id === f.pkg)
    && (!f.dueOnly || isDue(l)) && (!q || [l.name, l.phone, l.email, l.package_name].some((v) => (v || "").toLowerCase().includes(q)));
};

export default function Leads() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { packages } = useSiteData();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() => localStorage.getItem("owt_leads_view") || "board");
  const [sort, setSort] = useState("created_desc");
  const [f, setFilters] = useState({ status: "all", trip: "all", source: "all", dest: "all", pkg: "all", q: "", dueOnly: params.get("view") === "followups" });
  const [bookingFrom, setBookingFrom] = useState(null);
  const [adding, setAdding] = useState(false);
  const setF = (k) => (v) => setFilters((s) => ({ ...s, [k]: v }));

  const load = useCallback(() => { api.get("/admin/enquiries").then((r) => setItems(r.data)).finally(() => setLoading(false)); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { localStorage.setItem("owt_leads_view", view); }, [view]);
  const shown = useMemo(() => sortLeads(items.filter((l) => matches(l, f)), sort), [items, f, sort]);

  const replace = (e) => setItems((list) => list.map((x) => (x.id === e.id ? e : x)));
  const changeStatus = async (lead, status) => {
    const body = { status, ...(status === "follow_up" && !lead.follow_up_date ? { follow_up_date: tomorrowISO() } : {}) };
    try {
      const { data } = await api.patch(`/admin/enquiries/${lead.id}`, body);
      replace(data);
      toast.success(body.follow_up_date ? "Moved to Follow-up (tomorrow). Open the lead to change the date." : "Stage updated");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/admin/enquiries/${id}`);
    setItems((list) => list.filter((x) => x.id !== id));
    toast.success("Lead deleted");
  };
  const handlers = { onOpen: (l) => navigate(`/admin/leads/${l.id}`), onStatus: changeStatus, onBook: setBookingFrom, onDelete: remove };

  return (
    <div data-testid="admin-enquiries-page">
      <PageHeader title="Leads" subtitle="Every enquiry from the website, WhatsApp, Instagram, Facebook and Google, from New Lead to Booked.">
        <Button variant="outline" onClick={() => downloadLeadsCsv(shown)} data-testid="leads-export-button"><Download className="h-4 w-4 mr-2" />Export CSV</Button>
        <Button className="bg-ocean hover:bg-navy" onClick={() => setAdding(true)} data-testid="leads-add-button"><Plus className="h-4 w-4 mr-2" />Add lead</Button>
      </PageHeader>
      <LeadStats leads={items} dueOnly={f.dueOnly} setDueOnly={setF("dueOnly")} />
      <Panel className="p-3 mb-4 flex flex-col xl:flex-row gap-3 xl:items-center">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={f.q} onChange={(e) => setF("q")(e.target.value)} placeholder="Search name, mobile, package" className="pl-9 h-10" data-testid="enquiry-search-input" />
        </div>
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
          <Filter value={f.status} onChange={setF("status")} options={ENQ_STATUSES} all="All stages" testId="leads-filter-status" />
          <Filter value={f.trip} onChange={setF("trip")} options={TRIP_TYPES} all="All requirements" testId="leads-filter-trip" />
          <Filter value={f.source} onChange={setF("source")} options={LEAD_SOURCES} all="All sources" testId="leads-filter-source" />
          <Filter value={f.dest} onChange={setF("dest")} options={LEAD_DESTINATIONS} all="All destinations" testId="leads-filter-destination" />
          <Filter value={f.pkg} onChange={setF("pkg")} options={packages.map((p) => ({ value: p.id, label: p.name }))} all="All packages" testId="leads-filter-package" />
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="h-10 w-full sm:w-[190px]" data-testid="leads-sort-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{SORTS.map((o) => <SelectItem key={o.value} value={o.value}>Sort: {o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="inline-flex rounded-xl border border-slate-200 p-1 self-start">
          {[["board", KanbanSquare, "Board"], ["list", List, "List"]].map(([v, Icon, label]) => (
            <button key={v} type="button" onClick={() => setView(v)} data-testid={`leads-view-${v}`}
              className={`h-8 px-3 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${view === v ? "bg-ocean text-white" : "text-slate-600 hover:bg-slate-100"}`}><Icon className="h-4 w-4" />{label}</button>
          ))}
        </div>
      </Panel>
      {!loading && shown.length === 0 ? <Panel><Empty testId="enquiries-empty">No leads match these filters.</Empty></Panel>
        : view === "board" ? <LeadBoard leads={shown} {...handlers} />
          : <Panel className="overflow-x-auto"><LeadTable leads={shown} {...handlers} /></Panel>}
      <AddLeadDialog open={adding} onOpenChange={setAdding} onSaved={(l) => setItems((list) => [l, ...list])} />
      <BookingDialog open={!!bookingFrom} onOpenChange={(o) => !o && setBookingFrom(null)} enquiry={bookingFrom}
        onSaved={() => { setBookingFrom(null); load(); }} />
    </div>
  );
}
